import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpenIcon, ChevronDownIcon, DumbbellIcon, PlayIcon } from "lucide-react";
import type { Course, Skills } from "../../types/learning";
import { useLearner } from "../../contexts/LearnerContext";
import { PairChips } from "../ui/LangChip";
import { ProgressBar } from "../ui/ProgressBar";
import { BrutalButton } from "../ui/BrutalButton";
import { courseTitle, nextLevel } from "../../utils/language";
import { currentLesson, skillLabels, weakestSkill } from "../../utils/course";
import { bookPath, lessonPath, practicePath } from "../../utils/routes";
import { spring } from "../../utils/motion";
interface CourseRowProps {
  course: Course;
  open: boolean;
  onToggle: () => void;
}
export function CourseRow({
  course,
  open,
  onToggle
}: CourseRowProps) {
  const {
    setActiveCourseId
  } = useLearner();
  const {
    unit,
    lesson
  } = currentLesson(course);
  const weak = weakestSkill(course.skills);
  const panelId = `course-${course.id}`;
  return <li data-id="element-137" data-mp="3fzckropysvg6">
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={panelId} className="focus-ring grid w-full grid-cols-[auto_1fr_auto] items-center gap-4 rounded-xl px-3 py-3.5 text-left transition-colors duration-150 hover:bg-sunken md:grid-cols-[auto_1.4fr_1fr_auto_auto]" data-id="element-138" data-mp="3fyjnrfgt5kb8">
        <PairChips target={course.target} base={course.base} data-id="element-139" data-mp="2l83rpr8altk" />
        <span className="min-w-0" data-id="element-140" data-mp="2lzqeb2ul97p">
          <span className="block truncate text-[15px] font-bold text-ink" data-id="element-141" data-mp="7kaxie1rd5m8">{courseTitle(course)}</span>
          <span className="block truncate text-sm text-ink-2" data-id="element-142" data-mp="3osok2eepnny5">
            {course.level} · Unit {unit.number} · {course.lastStudied}
          </span>
        </span>
        <span className="hidden md:block" data-id="element-143" data-mp="3tpogdxlrdcc2">
          <ProgressBar value={course.progress} size="sm" label={`${course.progress}% to ${nextLevel(course.level)}`} data-id="element-144" data-mp="2dcrjfsxiffl3" />
          <span className="mt-1 block text-xs text-ink-2" data-id="element-145" data-mp="2idf7q6uctibm">
            {course.progress}% to {nextLevel(course.level)}
          </span>
        </span>
        <span className="hidden text-right md:block" data-id="element-146" data-mp="ovg9nvnapilu">
          <span className="block text-[15px] font-bold text-ink" data-id="element-147" data-mp="jvll3yqzt53y">{course.wordsLearned}</span>
          <span className="block text-xs text-ink-2" data-id="element-148" data-mp="f2se1481xkop">words</span>
        </span>
        <motion.span animate={{
        rotate: open ? 180 : 0
      }} transition={spring.snappy} className="text-ink-2" data-id="element-149" data-mp="a58ze37fz5w8">
          <ChevronDownIcon className="h-5 w-5" strokeWidth={2.5} data-id="element-150" data-mp="55we3mw7y6vr" />
        </motion.span>
      </button>

      <AnimatePresence initial={false} data-id="element-151" data-mp="13sycm2iog4o">
        {open && <motion.div id={panelId} initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        transition: {
          duration: 0.1
        }
      }} transition={spring.stable} data-id="element-152" data-mp="3sac9618cs62z">
            <div className="grid gap-5 px-3 pb-5 pt-1 md:grid-cols-[1fr_auto] md:items-end" data-id="element-153" data-mp="1qho37k4br4ji">
              <dl className="grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-id="element-154" data-mp="1lkxop1864h07">
                {(Object.keys(course.skills) as (keyof Skills)[]).map(k => <div key={k} className="grid grid-cols-[88px_1fr_32px] items-center gap-2 text-sm" data-id="element-155" data-mp="1y2i6r1v8gk5w">
                    <dt className={k === weak ? "font-bold text-ink" : "text-ink-2"} data-id="element-156" data-mp="22t8akao0geyh">{skillLabels[k]}</dt>
                    <dd data-id="element-157" data-mp="27s1rkd2iao3e">
                      <ProgressBar value={course.skills[k]} size="sm" tone={k === weak ? "accent" : "brand"} label={skillLabels[k]} data-id="element-158" data-mp="27sl0x5e3c45t" />
                    </dd>
                    <dd className="text-right font-semibold text-ink" data-id="element-159" data-mp="1a6x9kywhsc15">{course.skills[k]}</dd>
                  </div>)}
              </dl>
              <div className="flex flex-wrap gap-3" data-id="element-160" data-mp="1k1xn63hhukbc">
                <span onClick={() => setActiveCourseId(course.id)} data-id="element-161" data-mp="1p1tt489r4ltj">
                  <BrutalButton to={lessonPath(course, lesson.id)} size="sm" data-id="element-162" data-mp="1p34wi6zabrk4">
                    <PlayIcon className="h-3.5 w-3.5 fill-current" data-id="element-163" data-mp="2vh46hd5ra2u7" /> Resume
                  </BrutalButton>
                </span>
                <BrutalButton to={bookPath(course)} variant="surface" size="sm" data-id="element-164" data-mp="30j39glxl2xqm">
                  <BookOpenIcon className="h-4 w-4" data-id="element-165" data-mp="2y2loqelfyjg4" /> Read the book
                </BrutalButton>
                <BrutalButton to={practicePath(course)} variant="surface" size="sm" data-id="element-166" data-mp="2u2bayfreqoqp">
                  <DumbbellIcon className="h-4 w-4" data-id="element-167" data-mp="381avbb7w0hh2" /> Practise weak words
                </BrutalButton>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </li>;
}